import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  DatabaseBackup, Loader2, RefreshCw, PlugZap, CloudUpload, CloudDownload,
  TriangleAlert, CheckCircle2, History, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useSite } from "@/context/SiteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_REMOTE = "wmm-sync-remote-url";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};

/**
 * Preview <-> Production database replication console.
 * Production deploys start with an EMPTY MongoDB - after each deploy, PUSH
 * replicates every table there. PULL refreshes this developer copy from
 * production and is intentionally hard to trigger (checkbox + typing WIPE).
 */
export default function DataSyncPage() {
  const { refreshSites } = useSite();
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(false);

  const [remoteUrl, setRemoteUrl] = useState(() => localStorage.getItem(LS_REMOTE) || "");
  const [probing, setProbing] = useState(false);
  const [remote, setRemote] = useState(null);

  const [pushAck, setPushAck] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  const [pullAck, setPullAck] = useState(false);
  const [wipeText, setWipeText] = useState("");
  const [pulling, setPulling] = useState(false);
  const [pullResult, setPullResult] = useState(null);

  const loadSummary = useCallback(async () => {
    setSummaryError(false);
    try {
      const { data } = await axios.get(`${API}/sync/summary`);
      setSummary(data);
    } catch (e) {
      setSummaryError(true);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const saveRemote = (v) => {
    setRemoteUrl(v);
    localStorage.setItem(LS_REMOTE, v);
    setRemote(null);
  };

  const probe = async () => {
    setProbing(true);
    setRemote(null);
    try {
      const { data } = await axios.post(`${API}/sync/probe`, { remote_url: remoteUrl });
      setRemote(data.remote);
      toast.success("Remote environment reachable.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reach the remote environment.");
    } finally {
      setProbing(false);
    }
  };

  const doPush = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const { data } = await axios.post(`${API}/sync/push`, { remote_url: remoteUrl, confirm: true });
      setPushResult(data);
      setPushAck(false);
      loadSummary();
      toast.success(`Pushed ${Object.values(data.pushed_counts).reduce((a, b) => a + b, 0)} documents to production.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Push failed.");
    } finally {
      setPushing(false);
    }
  };

  const doPull = async () => {
    setPulling(true);
    setPullResult(null);
    try {
      const { data } = await axios.post(`${API}/sync/pull`, { remote_url: remoteUrl, confirm_text: wipeText.trim() });
      setPullResult(data);
      setPullAck(false);
      setWipeText("");
      loadSummary();
      refreshSites();
      toast.success(`Developer copy refreshed — ${data.local_result.total_imported} documents imported.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Pull failed.");
    } finally {
      setPulling(false);
    }
  };

  const remoteReady = remoteUrl.trim().length >= 8;
  const pullArmed = pullAck && wipeText.trim() === "WIPE";

  return (
    <div data-testid="data-sync-page" className="mx-auto max-w-4xl px-4 pb-20 pt-[104px] sm:px-6 sm:pt-16">
      <div className="flex items-center gap-2">
        <DatabaseBackup size={18} className="text-[#0050c0]" />
        <h1 className="font-nunito text-lg font-extrabold text-[#383838]">Data Sync</h1>
      </div>
      <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-[#6e6e6e]">
        Production deployments start with an <b>empty database</b>. After each deploy, push this preview's
        data to production so nothing is lost. Pull refreshes this developer copy from production — a
        destructive operation guarded by an explicit WIPE confirmation.
      </p>

      {/* Local inventory */}
      <div data-testid="sync-local-card" className="mt-5 rounded-[14px] border border-black/10 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">This Environment</h2>
          <button
            type="button"
            data-testid="sync-summary-refresh"
            onClick={loadSummary}
            aria-label="Refresh inventory"
            className="ml-auto text-[#8a8a8a] transition-colors duration-150 hover:text-[#383838]"
          >
            <RefreshCw size={13} />
          </button>
        </div>
        {summary === null && !summaryError ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Skeleton className="h-9 w-36 rounded-[8px]" />
            <Skeleton className="h-9 w-36 rounded-[8px]" />
            <Skeleton className="h-9 w-36 rounded-[8px]" />
          </div>
        ) : summaryError ? (
          <p className="mt-3 text-[12.5px] text-[#c25a00]">Could not load the local inventory — is the backend running?</p>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap gap-2">
              {summary.collections.map((c) => (
                <div key={c.collection} data-testid={`sync-collection-${c.collection}`} className="rounded-[8px] border border-black/10 bg-[#fafafa] px-3 py-1.5">
                  <span className="font-mono text-[11px] text-[#6e6e6e]">{c.collection}</span>
                  <span className="ml-2 font-nunito text-[12px] font-extrabold text-[#383838]">{c.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <p data-testid="sync-total-docs" className="mt-2 text-[11.5px] text-[#9a9a9a]">
              {summary.total_documents.toLocaleString()} documents total · regenerable caches excluded ({summary.excluded.join(", ")})
              {!summary.sync_key_configured && (
                <span className="ml-2 font-bold text-[#c25a00]">SYNC_KEY missing in backend/.env!</span>
              )}
            </p>
          </>
        )}
      </div>

      {/* Remote environment */}
      <div data-testid="sync-remote-card" className="mt-4 rounded-[14px] border border-black/10 bg-white p-4 shadow-sm">
        <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Counterpart Environment</h2>
        <p className="mt-1 text-[12.5px] text-[#6e6e6e]">
          The other copy of this app (e.g. your deployed production URL). Both environments must share the same SYNC_KEY.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[280px] flex-1 space-y-1">
            <Label className="font-nunito text-[10px] font-bold uppercase tracking-wide text-[#6e6e6e]">Remote app URL</Label>
            <Input
              data-testid="sync-remote-url-input"
              placeholder="https://your-app.emergent.host"
              value={remoteUrl}
              onChange={(e) => saveRemote(e.target.value)}
            />
          </div>
          <Button
            type="button"
            data-testid="sync-probe-button"
            onClick={probe}
            disabled={!remoteReady || probing}
            variant="outline"
            className="rounded-[10px] border-black/20 font-nunito text-xs font-extrabold uppercase tracking-wide text-[#383838]"
          >
            {probing ? <Loader2 size={13} className="animate-spin" /> : <PlugZap size={13} />}
            Test Connection
          </Button>
        </div>
        {remote && (
          <p data-testid="sync-remote-status" className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold text-[#0050c0]">
            <CheckCircle2 size={13} /> Reachable — {remote.total_documents.toLocaleString()} documents in {remote.collections.length} collections
          </p>
        )}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {/* PUSH */}
        <div data-testid="sync-push-card" className="flex flex-col rounded-[14px] border border-black/10 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <CloudUpload size={15} className="text-[#0050c0]" />
            <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Push → Production</h2>
          </div>
          <p className="mt-1.5 flex-1 text-[12.5px] leading-relaxed text-[#6e6e6e]">
            Replicates <b>every table</b> from this preview into the remote environment — sites, pages,
            deploy history, leads, everything. Remote data is completely replaced. Run this after each
            production deploy.
          </p>
          <label className="mt-3 flex items-start gap-2 rounded-[10px] border border-black/10 bg-[#fafafa] p-2.5">
            <Checkbox
              data-testid="sync-push-ack-checkbox"
              checked={pushAck}
              onCheckedChange={(v) => setPushAck(!!v)}
              className="mt-0.5"
            />
            <span className="text-[11.5px] leading-snug text-[#383838]">
              I understand this <b>overwrites all data</b> in the remote environment with this copy.
            </span>
          </label>
          <Button
            type="button"
            data-testid="sync-push-button"
            onClick={doPush}
            disabled={!remoteReady || !pushAck || pushing}
            className="mt-3 rounded-[10px] bg-[#0050c0] font-nunito text-xs font-extrabold uppercase tracking-wide text-white hover:bg-[#0343a0]"
          >
            {pushing ? <Loader2 size={13} className="animate-spin" /> : <CloudUpload size={13} />}
            {pushing ? "Replicating…" : "Push All Data"}
          </Button>
          {pushResult && (
            <p data-testid="sync-push-result" className="mt-2 text-[11.5px] font-bold text-[#0050c0]">
              Pushed {Object.values(pushResult.pushed_counts).reduce((a, b) => a + b, 0).toLocaleString()} documents
              across {Object.keys(pushResult.pushed_counts).length} collections.
            </p>
          )}
        </div>

        {/* PULL (danger) */}
        <div data-testid="sync-pull-card" className="flex flex-col rounded-[14px] border-2 border-[#f07000]/50 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <CloudDownload size={15} className="text-[#c25a00]" />
            <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#c25a00]">Pull ← Production</h2>
            <span className="ml-auto inline-flex items-center gap-1 rounded-[6px] bg-[#fef2e5] px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.12em] text-[#c25a00]">
              <ShieldAlert size={10} /> Destructive
            </span>
          </div>
          <p className="mt-1.5 flex-1 text-[12.5px] leading-relaxed text-[#6e6e6e]">
            Refreshes this <b>developer copy</b> from the remote environment. Every local table is wiped
            and replaced — unsynced local work is gone forever.
          </p>
          <label className="mt-3 flex items-start gap-2 rounded-[10px] border border-[#f07000]/40 bg-[#fef2e5]/60 p-2.5">
            <Checkbox
              data-testid="sync-pull-ack-checkbox"
              checked={pullAck}
              onCheckedChange={(v) => setPullAck(!!v)}
              className="mt-0.5"
            />
            <span className="text-[11.5px] leading-snug text-[#383838]">
              I understand this <b>permanently wipes every table</b> in this developer copy.
            </span>
          </label>
          <div className="mt-2 space-y-1">
            <Label className="font-nunito text-[10px] font-bold uppercase tracking-wide text-[#c25a00]">Type WIPE to confirm</Label>
            <Input
              data-testid="sync-pull-wipe-input"
              placeholder="WIPE"
              value={wipeText}
              onChange={(e) => setWipeText(e.target.value)}
              className="border-[#f07000]/40 font-mono uppercase focus-visible:ring-[#f07000]"
            />
          </div>
          <Button
            type="button"
            data-testid="sync-pull-button"
            onClick={doPull}
            disabled={!remoteReady || !pullArmed || pulling}
            variant="outline"
            className="mt-3 rounded-[10px] border-[#f07000]/60 font-nunito text-xs font-extrabold uppercase tracking-wide text-[#c25a00] hover:bg-[#fef2e5]"
          >
            {pulling ? <Loader2 size={13} className="animate-spin" /> : <TriangleAlert size={13} />}
            {pulling ? "Wiping & importing…" : "Wipe & Pull From Remote"}
          </Button>
          {pullResult && (
            <p data-testid="sync-pull-result" className="mt-2 text-[11.5px] font-bold text-[#0050c0]">
              Developer copy refreshed — {pullResult.local_result.total_imported.toLocaleString()} documents imported.
            </p>
          )}
        </div>
      </div>

      {/* History */}
      {summary?.history?.length > 0 && (
        <div data-testid="sync-history-card" className="mt-4 rounded-[14px] border border-black/10 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <History size={14} className="text-[#8a8a8a]" />
            <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Recent Syncs</h2>
          </div>
          <Separator className="my-2 bg-black/10" />
          <div className="space-y-1.5">
            {summary.history.map((h) => (
              <div key={h.id} className="flex flex-wrap items-center gap-2 text-[12px] text-[#6e6e6e]">
                <span className={`rounded-[5px] border px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.1em] ${
                  h.direction === "pull" ? "border-[#f8ddc0] bg-[#fef2e5] text-[#c25a00]" : "border-[#c8dcf7] bg-[#e9f1fd] text-[#0050c0]"
                }`}>
                  {h.direction}
                </span>
                <span className="font-bold text-[#383838]">{(h.total ?? 0).toLocaleString()} docs</span>
                {h.remote && <span className="truncate font-mono text-[10.5px]">{h.remote}</span>}
                <span className="ml-auto text-[11px] text-[#9a9a9a]">{fmtWhen(h.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
